/**
 * The ascent ladder — the page's eyebrow label, and its altimeter.
 *
 * Every section used to open with the same anonymous caps label, so six
 * sections in you had no idea whether you were near the end. This replaces it
 * with the label plus a tiny rising bar chart: one tick per section, heights
 * climbing left to right, the current one lit.
 *
 * It is a progress indicator that costs 32×12 pixels and never needs a scroll
 * listener, and it repeats the family's one motif — things go up — at the
 * smallest size on the page.
 */
const TONES = {
  // On paper: ink for the passed and current ticks, sand for what is ahead.
  light: { idx: "text-stone-600", label: "text-stone-700", on: "#a83c19", past: "#c2b8a6", next: "#e4dbcc" },
  // On night: the lit tick uses dawn, which is the light source for the whole
  // page — so the marker glows from the same sun as the section behind it.
  dark: { idx: "text-white/35", label: "text-white/45", on: "#ffc39d", past: "rgba(255,255,255,0.42)", next: "rgba(255,255,255,0.16)" },
};

export default function SectionMark({ index, total = 6, tone = "light", children }) {
  const c = TONES[tone] || TONES.light;
  const ticks = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <div className="flex items-center gap-3.5" data-testid="section-mark">
      <span className={`font-mono text-[11px] tabular-nums ${c.idx}`}>
        {String(index).padStart(2, "0")}
      </span>
      <svg
        width={(total - 1) * 6 + 2}
        height="12"
        viewBox={`0 0 ${(total - 1) * 6 + 2} 12`}
        aria-hidden="true"
        className="shrink-0 overflow-visible"
      >
        {ticks.map((n, i) => {
          const h = 3 + i * 1.5;
          const state = n === index ? "on" : n < index ? "past" : "next";
          return (
            <line
              key={n}
              x1={i * 6 + 1}
              x2={i * 6 + 1}
              y1={11}
              y2={11 - h}
              stroke={c[state]}
              strokeWidth={n === index ? 2 : 1.5}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <span className={`text-xs tracking-[0.2em] uppercase font-bold ${c.label}`}>
        {children}
      </span>
    </div>
  );
}
